/**
 * A skill's version history: every saved edit with its note and author,
 * the chosen version's text beside the current one, and (for editors) a
 * "Revert" that restores it as a new version. Nothing is ever lost.
 */
import { History, RotateCcw } from "lucide-react";
import { useEffect, useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { EmptyState } from "@/components/ui/empty-state";
import { Spinner } from "@/components/ui/spinner";
import { firmKnowledgeApi, type FirmScope } from "@/api/firm-knowledge";
import { cn } from "@/lib/utils";

import type {
  FirmSkill,
  FirmSkillVersion
} from "@/features/firmKnowledge/types";
import { fullDate, shortDate } from "./skillModel";

type Props = {
  skill: FirmSkill;
  scope: FirmScope;
  canEdit: boolean;
  /** Called with the reverted skill (a new version) after a revert. */
  onReverted: (skill: FirmSkill) => void;
};

const Column: FC<{
  heading: string;
  version: Pick<
    FirmSkillVersion,
    "title" | "description" | "instructions" | "inputs" | "authority"
  >;
  muted?: boolean;
}> = ({ heading, version, muted }) => (
  <div
    className={cn(
      "min-w-0 flex-1 space-y-3 rounded-xl ring-1 ring-line-2 p-3",
      muted && "bg-fill-1"
    )}
  >
    <h4 className="text-base font-medium text-fg">{heading}</h4>
    <div>
      <div className="text-sm text-fg-3">Title</div>
      <div className="text-sm font-medium">{version.title ?? ""}</div>
    </div>
    <div>
      <div className="text-sm text-fg-3">Description</div>
      <p className="whitespace-pre-wrap text-sm">{version.description ?? ""}</p>
    </div>
    {version.inputs?.length ? (
      <div>
        <div className="text-sm text-fg-3">Asks First</div>
        <ul className="list-inside list-disc text-sm">
          {version.inputs.map((i) => (
            <li key={i.name}>{i.prompt}</li>
          ))}
        </ul>
      </div>
    ) : null}
    {version.authority?.confirm_before?.length ? (
      <div>
        <div className="text-sm text-fg-3">Confirms Before</div>
        <p className="text-sm">{version.authority.confirm_before.join(", ")}</p>
      </div>
    ) : null}
    <div>
      <div className="text-sm text-fg-3">Instructions</div>
      <pre className="whitespace-pre-wrap font-sans text-sm leading-6">
        {version.instructions ?? ""}
      </pre>
    </div>
  </div>
);

export const SkillHistory: FC<Props> = ({
  skill,
  scope,
  canEdit,
  onReverted
}) => {
  const [versions, setVersions] = useState<FirmSkillVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [detail, setDetail] = useState<FirmSkillVersion | null>(null);
  const [reverting, setReverting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setVersions(null);
    firmKnowledgeApi
      .listSkillVersions(skill.id, scope)
      .then((page) => {
        if (cancelled) return;
        setVersions(page.versions);
        setError(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(
          e instanceof Error ? e.message : "Could not load the history."
        );
      });
    return () => {
      cancelled = true;
    };
  }, [skill.id, skill.version, scope]);

  useEffect(() => {
    if (selected === null) {
      setDetail(null);
      return undefined;
    }
    let cancelled = false;
    firmKnowledgeApi
      .getSkillVersion(skill.id, selected, scope)
      .then((v) => {
        if (!cancelled) setDetail(v);
      })
      .catch((e: unknown) => {
        if (!cancelled)
          toast.error(
            e instanceof Error ? e.message : "Could not load that version."
          );
      });
    return () => {
      cancelled = true;
    };
  }, [selected, skill.id, scope]);

  const revert = async (version: number) => {
    setReverting(true);
    try {
      const updated = await firmKnowledgeApi.revertSkillVersion(
        skill.id,
        version,
        scope
      );
      onReverted(updated);
      setSelected(null);
      toast.success(`Reverted to version ${version}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not revert.");
    } finally {
      setReverting(false);
    }
  };

  if (error) {
    return <p className="px-6 py-6 text-base text-danger">{error}</p>;
  }
  if (versions === null) {
    return (
      <div className="flex items-center gap-2 px-6 py-6 text-base text-fg-3">
        <Spinner className="size-3.5" /> Loading history…
      </div>
    );
  }
  if (versions.length === 0) {
    return (
      <EmptyState
        icon={History}
        title="No History Yet"
        description="Each saved edit is kept here so any version can be brought back."
        className="py-10"
        data-testid="skill-history-empty"
      />
    );
  }

  return (
    <div
      className="flex min-h-0 max-w-240 flex-1 flex-col gap-4 px-6 py-5"
      data-testid="skill-history"
    >
      <ol className="flex flex-col divide-y divide-line-1 overflow-hidden rounded-xl bg-elevated ring-1 ring-line-2">
        {versions.map((v) => {
          const current = v.version === skill.version;
          const active = v.version === selected;
          return (
            <li key={v.id}>
              <button
                type="button"
                className={cn(
                  "flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors hover:bg-fill-1",
                  active && "bg-fill-2 hover:bg-fill-2"
                )}
                onClick={() => setSelected(current ? null : v.version)}
                data-testid={`skill-version-${v.version}`}
                aria-pressed={active}
              >
                <History className="mt-0.5 size-4 shrink-0 text-fg-3" />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2 text-base">
                    <span className="font-medium text-fg">
                      Version {v.version}
                    </span>
                    {current ? (
                      <StatusBadge tone="success">Current</StatusBadge>
                    ) : null}
                    <span className="text-fg-3">{v.change_note}</span>
                  </span>
                  <span className="block text-sm text-fg-3">
                    <span title={fullDate(v.updated_at)}>
                      {shortDate(v.updated_at)}
                    </span>
                    {v.updated_by_name ? ` · ${v.updated_by_name}` : ""}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      {selected !== null ? (
        <section className="space-y-3" data-testid="skill-version-compare">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base font-medium text-fg">
              Version {selected} Beside the Current Text
            </h3>
            {canEdit ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={reverting || !detail}
                onClick={() => void revert(selected)}
                data-testid="skill-version-revert"
              >
                <RotateCcw className="size-3.5" />
                {reverting ? "Reverting…" : `Revert to Version ${selected}`}
              </Button>
            ) : null}
          </div>
          {detail ? (
            <div className="flex flex-col gap-3 lg:flex-row">
              <Column heading={`Version ${selected}`} version={detail} muted />
              <Column heading={`Current (v${skill.version})`} version={skill} />
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-fg-3">
              <Spinner className="size-3.5" /> Loading version…
            </div>
          )}
        </section>
      ) : versions.length === 1 ? (
        <p className="text-base text-fg-3" data-testid="skill-history-hint">
          Each save keeps a version you can compare and bring back.
        </p>
      ) : (
        <p className="text-base text-fg-3">
          Pick an earlier version to see it beside the current text
          {canEdit ? " and bring it back" : ""}.
        </p>
      )}
    </div>
  );
};
